import { intervalToDuration } from 'date-fns'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { is } from 'immer/dist/internal'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PATH } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'

import style from './MissionStats.module.css'

const MissionStats = ({ robot }: { robot?: IRobotInfo }) => {
  const [isOpen, setIsOpen] = useState(false)

  const getDuration = (seconds: number) => {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 })

    // Format duration: show days if present, otherwise hours:minutes:seconds or minutes:seconds
    let formatted = ''
    if (duration.days && duration.days > 0) {
      formatted = `${duration.days}d:${duration.hours || 0}h:${duration.minutes || 0}m`
    } else if (duration.hours && duration.hours > 0) {
      formatted = `${duration.hours}h:${duration.minutes || 0}m:${duration.seconds || 0}s`
    } else if (duration.minutes && duration.minutes > 0) {
      formatted = `${duration.minutes}m:${duration.seconds || 0}s`
    } else {
      // Show seconds if less than a minute
      formatted = `${duration.seconds || 0}s`
    }

    return formatted
  }

  let job
  const isSimpleTask = robot ? robot?.job?.type?.trim() === 'Task' : true
  let missionSequenceNumber = 0
  let missionsListLength = 0
  let missionOrder = ''
  if (isSimpleTask) {
    job = robot?.robotRaw?.missions_jobs.find(job => job.id === robot.robotRaw.last_job_id)
  } else {
    job = robot?.robotRaw?.odysseys.find(job => job.id === robot?.job?.id)
    missionSequenceNumber = robot?.robotRaw?.last_mission_sequence_number || 0
    missionsListLength = job?.missions.length || 0
    missionOrder = `${missionSequenceNumber + 1}/${missionsListLength}`
  }
  const missionStats = job?.mission_status_last?.mission_progress

  const totalMissionStats = missionStats?.overall_mission_feedback

  const totalPercents =
    totalMissionStats &&
    totalMissionStats.progress !== undefined &&
    totalMissionStats.overall !== undefined &&
    totalMissionStats.overall
      ? Math.round((totalMissionStats.progress / totalMissionStats.overall) * 100)
      : '-'
  const totalMeters =
    totalMissionStats &&
    totalMissionStats.progress !== undefined &&
    totalMissionStats.overall !== undefined &&
    totalMissionStats.overall
      ? `${Math.round(totalMissionStats.progress)}m/${Math.round(totalMissionStats.overall)}m`
      : '-/-'
  const totalTime =
    totalMissionStats?.forecast !== undefined &&
    totalMissionStats?.forecast > 0 &&
    totalMissionStats &&
    totalMissionStats.progress !== undefined
      ? getDuration(totalMissionStats.forecast)
      : '-'

  const totalOdysseyStats = job?.mission_status_last?.odyssey_progress?.overall_mission_feedback
  const totalOdysseyPercents =
    totalOdysseyStats &&
    totalOdysseyStats.progress !== undefined &&
    totalOdysseyStats.overall !== undefined &&
    totalOdysseyStats.overall
      ? Math.round((totalOdysseyStats.progress / totalOdysseyStats.overall) * 100)
      : '-'
  const totalOdysseyMeters =
    totalOdysseyStats &&
    totalOdysseyStats.progress !== undefined &&
    totalOdysseyStats.overall !== undefined &&
    totalOdysseyStats.overall
      ? `${Math.round(totalOdysseyStats.progress)}m/${Math.round(totalOdysseyStats.overall)}m`
      : '-/-'
  const totalOdysseyTime =
    totalOdysseyStats?.forecast !== undefined &&
    totalOdysseyStats?.forecast > 0 &&
    totalOdysseyStats &&
    totalOdysseyStats.progress !== undefined
      ? getDuration(totalOdysseyStats.forecast)
      : '-'

  const zoneStats = missionStats?.coverage_task_feedback
  const zoneMaxPossible = zoneStats && zoneStats.forecast ? `${Math.round(zoneStats.forecast)}%` : '-'
  const zoneProgress = zoneStats && zoneStats.progress ? `${Math.round(zoneStats.progress)}%` : '-'
  const zoneArea = zoneStats && zoneStats.overall ? `${Math.round(zoneStats.overall)}m\u00B2` : '-'

  const routeStats = missionStats?.moving_task_feedback
  const routeMetersTraveled = routeStats && routeStats.progress ? `${Math.round(routeStats.progress)}m` : '-'
  const routeTimeLeft =
    routeStats?.forecast !== undefined && routeStats?.forecast > 0 && routeStats && routeStats.overall
      ? getDuration(routeStats.forecast)
      : '-'
  const routeMetersTotal = routeStats && routeStats.overall ? `${Math.round(routeStats.overall)}m` : '-'

  return (
    <div className={style.root}>
      <div className={style.additionalInfoHeader}>
        <div className={style.headerGrid}>
          <span className={style.semibold}>{'Task:'}</span>
          <span>{robot?.job?.id || 0}</span>
          <span></span>
          <span></span>
          <span></span>
          <span>
            <span className={style.semibold}>Mission: </span>
          </span>
          <Link
            to={`${PATH.MISSION}${robot?.job?.missionId || 0}`}
            style={{ textDecoration: 'none', color: '#22366d' }}
          >
            {robot?.job?.missionId || 0}
          </Link>

          {!isSimpleTask && missionsListLength > 1 ? <span>{missionOrder}</span> : <span></span>}
          <span></span>
          <span></span>

          {/* Overall progress (Odyssey) */}
          {!isSimpleTask && missionsListLength > 1 && (
            <>
              <span className={style.semibold}>Overall progress:</span>
              <span>{totalOdysseyPercents}%</span>
              <span>{totalOdysseyMeters}</span>
              <span className={style.semibold}>Time left:</span>
              <span className={style.textInfo}>{totalOdysseyTime}</span>
            </>
          )}

          {/* Mission progress */}
          <span className={style.semibold}>Mission progress:</span>
          <span>{totalPercents}%</span>
          <span>{totalMeters}</span>
          <span className={style.semibold}>Time left:</span>
          <span className={style.textInfo}>{totalTime}</span>
        </div>
        <div className={style.details} onClick={() => setIsOpen(!isOpen)}>
          <span></span>
          <ArrowSvg className={`${style.icon} ${isOpen ? style.rotate : ''}`} />
        </div>
      </div>
      {isOpen && (
        <div className={style.twoColumns}>
          <div className={style.column}>
            <div className={style.subtitle}>Current zone</div>
            <div>
              Max possible coverage: <span className={style.bold}>{zoneMaxPossible}</span>
            </div>
            <div>
              Zone cleaned: <span className={style.bold}>{zoneProgress}</span>
            </div>
            <div>
              Zone area: <span className={style.bold}>{zoneArea}</span>
            </div>
          </div>
          <div className={style.column}>
            <div className={style.subtitle}>Current route</div>
            <div>
              Distance: <span className={style.bold}>{routeMetersTotal}</span>
            </div>
            <div>
              Distance traveled: <span className={style.bold}>{routeMetersTraveled}</span>
            </div>
            <div>
              Time left: <span className={style.bold}>{routeTimeLeft}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MissionStats
